import React, { useState, useEffect, useContext, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Typography, Paper, Alert, CircularProgress } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material"
import { useAuth } from "../contexts/AuthContext";
import { useTabGuard } from "../contexts/TabGuardContext";
import { CustomTextField } from '../styles/SharedStyles';
import { WithdrawalContext } from '../contexts/WithdrawalContext';
import DuplicateTabScreen from '../components/DuplicateTabScreen';

const passwordFieldLoadingSx = {
    '& .MuiIconButton-root': { color: 'var(--disabled-color)', opacity: 1 },
    '& .MuiIconButton-root.Mui-disabled': { opacity: 1, color: 'var(--disabled-color)' },
    '& .MuiIconButton-root .MuiSvgIcon-root': { color: 'var(--disabled-color)', opacity: 1 },
};

const Login = () => {
    const [showPassword, setShowPassword] = React.useState(false);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [loggingIn, setLoggingIn] = useState(false);
    const [sessionConflict, setSessionConflict] = useState(false);
    const [isBrowserSessionTaken, setIsBrowserSessionTaken] = useState(false);
    const { login, isAuthenticated, isSessionTimedOut, isKickedOut } = useAuth();
    const { claimPrimary } = useTabGuard();
    const navigate = useNavigate();
    const location = useLocation();
    const { getProcessingWithdrawalId } = useContext(WithdrawalContext);

    // Extract the intended path from state or default to "/dashboard"
    const from = location.state?.from || "/dashboard";

    const navigateAfterLogin = useCallback(async () => {
        const withdrawalId = await getProcessingWithdrawalId();
        if (withdrawalId) {
            navigate('/payout', { replace: true });
        } else {
            navigate(from, { replace: true });
        }
    }, [getProcessingWithdrawalId, navigate, from]);

    // Redirect (with withdrawal check) if already authenticated — covers page refresh
    // and the scenario where Tab 2 becomes primary after Tab 1 closes.
    useEffect(() => {
        if (isAuthenticated) {
            navigateAfterLogin();
        } else if (isKickedOut) {
            setError("您的账号已在另一台设备登录，请重新登入");
        } else if (isSessionTimedOut) {
            setError("会话已超时，请重新登入");
        }
    }, [isAuthenticated, isSessionTimedOut, isKickedOut, navigateAfterLogin]);

    // Detect when another tab in this browser logs in while we are on the login page.
    // The storage event fires in every tab except the one that wrote the value.
    useEffect(() => {
        const handleStorage = (e) => {
            if (e.key !== 'accessToken') return;
            setIsBrowserSessionTaken(!!e.newValue);
        };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);

    const handleLogin = async (e) => {
        e?.preventDefault();

        setError(null);

        if (!username || !password) {
            setError("用户名和密码是必填项!");
            return;
        }

        setLoggingIn(true);
        try {
            const result = await login(username, password);
            if (result.success) {
                claimPrimary();
                await navigateAfterLogin();
            } else if (result.sessionConflict) {
                setSessionConflict(true);
            } else {
                setError("用户名或密码错误");
            }
        } catch (err) {
            console.error("Login error:", err);
            setError("发生了一个错误，请稍后再试。");
        } finally {
            setLoggingIn(false);
        }
    };

    const handleForceLogin = async () => {
        setSessionConflict(false);
        setLoggingIn(true);
        try {
            const result = await login(username, password, true);
            if (result.success) {
                claimPrimary();
                await navigateAfterLogin();
            } else {
                setError("用户名或密码错误");
            }
        } catch (err) {
            console.error("Force login error:", err);
            setError("发生了一个错误，请稍后再试。");
        } finally {
            setLoggingIn(false);
        }
    };

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleMouseUpPassword = (event) => {
        event.preventDefault();
    };

    if (isBrowserSessionTaken) {
        return (
            <DuplicateTabScreen />
        );
    }

    return (
        <>
        <Box sx={{ 
            height: '100dvh', 
            maxWidth: '100%',
            backgroundColor: 'var(--bg-color)',
            backgroundImage: 'url("../assets/abstract-background.svg")', // Use the SVG file as background
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
        }}
        >
            <Paper elevation={6} sx={{ 
                borderRadius: 2,
                backgroundColor: 'rgba(255,255,255,0.2)',
                width: '-webkit-fill-available',
                maxWidth: '480px',
                padding: 3, 
                margin: 3,
            }}
            >
    
            <Typography
                variant="h4"
                align="center"
                fontWeight="bold"
                color="primary"
                gutterBottom
                marginBottom={3}
            >
            欢乐宝
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Box component="form" onSubmit={handleLogin} noValidate>
            <Box sx={{ mb: 2 }}>
            <CustomTextField
                fullWidth
                label="用户名"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loggingIn}
                autoComplete="username"
            />
            </Box>

            <Box sx={{ mb: 3 }}>
            <CustomTextField
                fullWidth
                label="密码"
                type={ showPassword ? 'text' : 'password' }
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loggingIn}
                autoComplete="current-password"
                sx={
                    loggingIn
                        ? passwordFieldLoadingSx
                        : { '& .Mui-focused .MuiIconButton-edgeEnd': { color: 'var(--primary-color)' } }
                }
                slotProps={{
                    input: {
                        endAdornment: 
                        <IconButton
                            type="button"
                            aria-label={
                                showPassword ? 'hide the password' : 'display the password'
                            }
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            onMouseUp={handleMouseUpPassword}
                            edge='end'
                            disabled={loggingIn}
                            sx={{
                                color: loggingIn ? 'var(--disabled-color)' : 'var(--secondary-color)',
                                ...(loggingIn
                                    ? {
                                          opacity: 1,
                                          '&.Mui-disabled': { opacity: 1, color: 'var(--disabled-color)' },
                                          '& .MuiSvgIcon-root': {
                                              color: 'var(--disabled-color)',
                                              opacity: 1,
                                          },
                                      }
                                    : {}),
                                '&:focus': {
                                    color: 'red',
                                },
                            }}
                        >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    },
                }}
            />
            </Box>

            <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={loggingIn}
                className="primary-button"
                sx={{
                    minHeight: 48,
                    ...(loggingIn && { '&.Mui-disabled': { opacity: 1 } }),
                }}
            >
                {loggingIn ? <CircularProgress size={24} thickness={4} sx={{ color: 'rgba(0,0,0,0.55)' }} /> : '登入'}
            </Button>
            </Box>
            </Paper>
        </Box>

        {/* Session conflict confirmation dialog */}
        <Dialog
            open={sessionConflict}
            onClose={() => setSessionConflict(false)}
            PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
        >
            <DialogTitle fontWeight="bold">账号已在其他设备登录</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    此账号当前已在另一台设备上登录。继续登录将强制退出另一台设备的会话。
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button
                    variant="outlined"
                    onClick={() => setSessionConflict(false)}
                    disabled={loggingIn}
                >
                    取消
                </Button>
                <Button
                    variant="contained"
                    className="primary-button"
                    onClick={handleForceLogin}
                    disabled={loggingIn}
                >
                    {loggingIn
                        ? <CircularProgress size={20} thickness={4} sx={{ color: 'rgba(0,0,0,0.55)' }} />
                        : '继续登录'}
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default Login;
